import { useMemo } from "react";
import { api } from "../api/api";
import { useApi } from "../core/hooks/useApi";
import { useData } from "../core/hooks/useData";
import * as pdf from "../core/pdf/types";
import { Table } from "../core/ui/Table";
import { first } from "../core/utils/array";
import { isArray } from "../core/utils/conversion";
import { getFirstObjectValue } from "../core/utils/objectPath";
import { DataEntryCustomScriptFunctions } from "./runScript";
import { createPdfScriptEngine } from "../core/pdf/runScript";
import { NamedScopeObject } from "../core/utils/script";

export interface RowRef {
	rowIndex: number,
	rowDef: pdf.TableRow,
	dataWindows: NamedScopeObject[],
};

export function InlineReport(props: {
	reportID: string,
	data: any,
}) {

	const def = useApi({
		request: () => api.report_def_get({
			id: props.reportID,
			dependencies: true,
		}),
		prepareResult: (result: pdf.report_def[]) => {
			const def = first(result);
			return def;
		}
	}, [props.reportID]) as pdf.report_def;

	if (!def) return null;

	return (
		<HtmlComposer
			structure={def.data}
			data={props.data}
		/>
	)
}


export function HtmlComposer(props: {
	structure: pdf.ReportStructure,
	data: any,
}) {
	const { structure } = props;

	const scriptEngine = useMemo(() => {

		const result = createPdfScriptEngine(
			undefined,
		);
		return result;

	}, []);

	let dataWindows = [
		{
			name: "data",
			obj: props.data,
		}
	] as NamedScopeObject[];

	function runScript(script: string) {
		return scriptEngine.run(
			script,
			undefined,
			dataWindows,
		);
	}

	function compose() {
		const result = [];

		for(let s of structure.sections) {
			result.push(section(s));
		}

		return (
			result
		);
	}

	function section(s: pdf.Section) {
		return content(s.content);
	}

	function content(content: pdf.AllContent[]) {
		const result = [];

		for(let c of content) {
			if (c.visibility) {
				const visible = runScript(c.visibility);
				if (visible !== true) continue;
			}


			if (c.type === "table") {
				result.push(table(c as pdf.Table));
			
			} else if (c.type === "p") {
				result.push(p(c as pdf.Paragraph));
			
			} else if (c.type === "heading") {
				result.push(heading(c as pdf.Heading));

			// } else if (c.type === "list") {
			// 	result.push(list(c as pdf.List));

			} else if (c.type === "tag") {
				result.push(tag(c as pdf.Tag));

			} else if (c.type === "group") {
				result.push(group(c as pdf.Group));

			}

		}
		return result;
	}

	function iterateOverList(def: pdf.IterationProps, renderItem: (item: any) => void) {
		const arr = runScript(`get("${def.list}")`);

		if (arr && isArray(arr)) {
			for(let item of arr) {
				dataWindows.push({
					name: def.scopeName!,
					obj: item,
				});

				let include = true;
				if (def.filter) {
					include = runScript(def.filter);
				}	

				if (include) {
					renderItem(item);
				}

				dataWindows.pop();
			}
		}
	}

	function group(g: pdf.Group): any {
		if (g.list) {
			const result = [] as any[];
			iterateOverList(g, (item) => {
				result.push(content(g.content));
			});
			return result;

		} else {
			return content(g.content);

		}
	}

	// function list(list: pdf.List) {
	// 	// NOTE: list items can be groups
	// 	const items = [];

	// 	let i = 0;
	// 	for(let item of list.items) {
	// 		if (item.list) {
	// 			// NOTE: this code is specifically for finding repeating containers
	// 			// in future may want to add a secondary check that just looks for any array from data contexts
	// 			// ... but this will have limited scripting ability available - i.e. get("") won't be run in 
	// 			// the context of each array element etc
	// 			updateScope();
	// 			const ref = scriptContext.container.find(item.list);
	// 			if (ref && ref.field.type === "container") {
	// 				const cont = ref.field as ContainerElement;
	// 				if (cont.repeat && cont.repeat.itemName) {
	// 					// repeating container, the child containers should be the instances
	// 					for (let childContainer of ref.container.childContainers) {
	// 						if (childContainer.def.id !== item.list) continue;
	// 						scope.push(childContainer);
	// 						items.push(
	// 							<li key={i}>{content(item.content)}</li>
	// 						);
	// 						scope.pop();
	// 						i++;
	// 					}
	// 				}
	// 			}

	// 		} else {
	// 			items.push(
	// 				<li key={i}>{content(item.content)}</li>
	// 			);
	// 			i++;
	// 		}
	// 	}

	// 	return (
	// 		<ul key={list.id}>{items}</ul>
	// 	);
	// }

	// function heading(heading: pdf.Heading) {
	// 	return (
	// 		<div key={heading.id} className={"h" + heading.level}>
	// 			{p(heading)}
	// 		</div>
	// 	)
	// }

	function p(para: pdf.Paragraph) {

		let style = {
			fontSize: (para.fontSize ? para.fontSize + "mm" : undefined),
			textAlign: (para.align ? para.align : undefined),
			fontWeight: (para.bold ? "bold" : undefined),
			fontStyle: (para.italic ? "italic" : undefined),
		};

		const spans = [];
		let i = 0;
		for(let seg of para.segments) {
			let text = seg.text;

			const f = seg.field;
			if (f) {
				if (f.script) {
					let scriptResult = runScript(f.script);

					// does this need to be formatted?
					if (f.format || f.forceCase) {
						scriptResult = DataEntryCustomScriptFunctions.formatter(scriptResult, f.format ? f.format : "", f.forceCase);
					}

					text = (scriptResult !== undefined) ? String(scriptResult) : "";
				}
			}

			spans.push(
				<span
					key={i}
					style={{
						fontWeight: (seg.bold ? "bold" : undefined),
						fontStyle: (seg.italic ? "italic" : undefined),
						whiteSpace: "pre-wrap",
					}}
				>{text}&nbsp;</span>
			);
		}

		return (
			<div
				key={para.id}
				style={style as any}
				className={para.className}
			>{spans.length > 0 ? spans : "\u00A0"}</div>
		)
	}

	function tag(tag: pdf.Tag) {
		const c = content(tag.content);
		return <div className="tag incomplete">{c}</div>
	}

	function heading(heading: pdf.Heading) {
		return <div className="font-large">{p(heading as pdf.Paragraph)}</div>
	}

	function table(table: pdf.Table) {
		const cols = table.cols as any[];
		let i = 0;
		for(let c of cols) {
			c.index = i++;
		}

		// NOTE: rows can be groups so have to preprocess and make sure have all the rows necessary
		let headerRow: any = undefined;
		const rows: RowRef[] = [];
		i = 0;

		for(let r of table.rows) {
			if (r.list) {
				iterateOverList(r, (item) => {
					if (r.visibility) {
						const visible = runScript(r.visibility);
						if (visible !== true) return;
					}			

					const rd = {
						rowIndex: -1,
						rowDef: r,
						dataWindows: [
							...dataWindows, 
							{
								name: r.scopeName!,
								obj: item,
							},
						],
					};

					if (!headerRow) {
						headerRow = rd;
					} else {
						rd.rowIndex = i++;
						rows.push(rd);
					}
				});

			} else {
				if (r.visibility) {
					const visible = runScript(r.visibility);
					if (visible !== true) continue;
				}

				const rd = {
					rowIndex: -1,
					rowDef: r,
					dataWindows: [
						...dataWindows,
					]
				} as any as RowRef;
				if (!headerRow) {
					headerRow = rd;
				} else {
					rd.rowIndex = i++;
					rows.push(rd);
				}

			}
		}

		return (
			<Table
				key={table.id}

				cols={cols}
				items={rows}
				//interactive={true}
				render={{
					//headerRow: () => null,

					colTitle: (c) => {
						if (headerRow) {
							const row = (headerRow as any) as RowRef;
							const cell = row.rowDef.cells[c.index];
	
							const orig = dataWindows;
							dataWindows = row.dataWindows;
								
							const result = content(cell.content);

							dataWindows = orig;
	
							return result;	
						}
					},

					colValue: (r, c) => {
						const row = (r as any) as RowRef;
						const cell = row.rowDef.cells[c.index];

						const orig = dataWindows;
						dataWindows = row.dataWindows;

						const result = content(cell.content);

						dataWindows = orig;

						return result;
					}
				}}
			/>
		);
	}

	const result = compose();

	return (
		<div
			className="html-composer"
		>
			{result}
		</div>
	);

}
